import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';
import { 
  Monitor, 
  Smartphone, 
  Maximize2, 
  X,
  ExternalLink,
  Eye,
  EyeOff,
  Check
} from 'lucide-react';
import PreviewContext from '../context/PreviewContext';
import { usePlan } from '../context/PlanContext';
import axios from '../utils/axiosInstance';
import { BASE_URL, ROOT_DOMAIN } from '../config/constants';
import { toast } from 'sonner';

interface Domain {
  _id: string;
  slug: string;
  url: string;
  name?: string;
  customDomain?: string | null;
  dnsVerified?: boolean;
}

type ViewMode = 'desktop' | 'mobile';

interface LivePreviewPanelProps {
  domain: Domain | null;
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  currentSection?: string;
  onThemeActivated?: () => void;
  refreshKey?: number;
}

const LivePreviewPanel: React.FC<LivePreviewPanelProps> = ({
  domain,
  isOpen,
  onClose,
  onToggle,
  currentSection,
  onThemeActivated,
  refreshKey,
}) => {
  const previewContext = useContext(PreviewContext);
  const previewTheme = previewContext?.previewTheme || null;
  const setPreviewTheme = previewContext?.setPreviewTheme;
  const suppressPreviewResetRef = previewContext?.suppressPreviewResetRef;
  const [activatingTheme, setActivatingTheme] = useState(false);
  const { canAdd, openUpgradeModal } = usePlan();
  const canActivateTheme = previewTheme?.isDefault || canAdd('themes');
  
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState<number>(Date.now());
  const DEFAULT_WIDTH = 500;
  const [panelWidth, setPanelWidth] = useState<number>(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousDomainRef = useRef<string | null>(null);
  const previousSectionRef = useRef<string | undefined>(undefined);

  const MIN_WIDTH = 400;
  const MAX_WIDTH_PERCENT = 0.9; // 90% of viewport

  // Build preview URL. Once the custom domain is DNS-verified, use it directly
  // (attachVercelDomain fires at verify-dns time so Vercel is already aware and
  // SSL provisions within seconds). Fall back to the stable subdomain otherwise.
  const getBaseUrl = useCallback(() => {
    if (!domain?.slug) return null;
    if (domain.dnsVerified && domain.customDomain) {
      return `https://${domain.customDomain}`;
    }
    return `https://${domain.slug}.${ROOT_DOMAIN}`;
  }, [domain?.slug, domain?.customDomain, domain?.dnsVerified]);

  // Build preview URL
  const getPreviewUrl = useCallback((_section?: string) => {
    const baseUrl = getBaseUrl();
    if (!baseUrl) return null;
    return baseUrl;
  }, [getBaseUrl]);

  const previewUrl = getPreviewUrl(currentSection);

  // Sync iframe src with preview URL
  useEffect(() => {
    setIframeSrc(previewUrl);
  }, [previewUrl]);

  // Manual refresh - reloads the iframe
  const handleRefresh = useCallback(() => {
    setIsLoading(true);
    setIframeSrc(null);
    setIframeKey(Date.now());
  }, []);

  // Send theme preview via postMessage — no iframe reload needed
  useEffect(() => {
    if (!iframeRef.current?.contentWindow) {
      // Clear suppression flag even when panel isn't ready, to avoid stale state
      if (suppressPreviewResetRef?.current) suppressPreviewResetRef.current = false;
      return;
    }
    const origin = getBaseUrl() ?? '*';
    if (previewTheme) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'theme-preview', theme: previewTheme.themeTitle },
        origin
      );
    } else {
      if (suppressPreviewResetRef?.current) {
        // Theme was just activated — keep the preview theme visible on the website;
        // the socket update will smoothly apply the now-active theme (same value, no flash).
        suppressPreviewResetRef.current = false;
      } else {
        iframeRef.current.contentWindow.postMessage(
          { type: 'theme-preview-reset' },
          origin
        );
      }
    }
  }, [previewTheme, domain?.slug, domain?.customDomain, domain?.dnsVerified]);

  // Handle activating the previewed theme
  const handleActivatePreviewTheme = useCallback(async () => {
    if (!previewTheme) return;
    setActivatingTheme(true);
    try {
      await axios.put(`${BASE_URL}/api/user-theme/${previewTheme.userThemeId}/activate`);
      toast.success('Theme activated successfully!');
      previewContext?.clearPreviewAfterActivation?.();
      previewContext?.triggerThemeActivated(previewTheme.userThemeId);
      onThemeActivated?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to activate theme');
    } finally {
      setActivatingTheme(false);
    }
  }, [previewTheme, setPreviewTheme, onThemeActivated, handleRefresh]);

  // Reset iframe when domain changes
  useEffect(() => {
    const domainKey = domain?.slug || null;
    if (domainKey && domainKey !== previousDomainRef.current) {
      previousDomainRef.current = domainKey;
      setIsLoading(true);
      setIframeKey(Date.now());
    }
  }, [domain?.slug]);

  // Reload iframe when refreshKey changes (e.g., after onboarding verification)
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      setIsLoading(true);
      setIframeSrc(null);
      setIframeKey(Date.now());
    }
  }, [refreshKey]);

  // Track section changes without reloading
  // Clear preview theme when navigating away from site-branding
  useEffect(() => {
    if (currentSection && currentSection !== 'siteBranding' && previewTheme) {
      setPreviewTheme?.(null);
    }
    if (currentSection) {
      previousSectionRef.current = currentSection;
    }
  }, [currentSection]);

  // Persist open/closed state and reset width when reopened
  useEffect(() => {
    localStorage.setItem('previewPanelOpen', JSON.stringify(isOpen));
    if (isOpen) {
      setPanelWidth(DEFAULT_WIDTH);
    }
  }, [isOpen]);

  // Persist expanded state
  useEffect(() => {
    localStorage.setItem('previewPanelExpanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  // Load persisted expanded state
  useEffect(() => {
    const savedExpanded = localStorage.getItem('previewPanelExpanded');
    if (savedExpanded) {
      try {
        setIsExpanded(JSON.parse(savedExpanded));
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  const handleIframeLoad = () => {
    setIsLoading(false);
    // Resend after load in case postMessage fired before iframe was ready
    if (previewTheme && iframeRef.current?.contentWindow) {
      const origin = getBaseUrl() ?? '*';
      iframeRef.current.contentWindow.postMessage(
        { type: 'theme-preview', theme: previewTheme.themeTitle },
        origin
      );
    }
  };

  const openInNewTab = () => {
    if (previewTheme && previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    } else {
      const baseUrl = getBaseUrl();
      if (baseUrl) {
        window.open(baseUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  // Handle resize drag
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const maxWidth = window.innerWidth * MAX_WIDTH_PERCENT;
      const newWidth = window.innerWidth - e.clientX;
      const clampedWidth = Math.min(Math.max(newWidth, MIN_WIDTH), maxWidth);
      setPanelWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, panelWidth]);

  // Don't render if no domain selected
  if (!domain) {
    return null;
  }

  // Collapsed state - show toggle button only (hidden on mobile)
  if (!isOpen) {
    return (
      <div className="fixed right-4 bottom-4 z-50 hidden lg:block">
        <Button
          onClick={onToggle}
          className="h-12 px-4 shadow-lg rounded-full flex items-center gap-2 bg-primary hover:bg-primary/90"
        >
          <Eye className="w-5 h-5" />
          <span className="font-medium">Live Website</span>
        </Button>
      </div>
    );
  }

  // Calculate backdrop opacity based on panel width (more width = more opacity)
  const getResizeBackdropOpacity = () => {
    const halfViewport = window.innerWidth * 0.5;
    const maxWidth = window.innerWidth * MAX_WIDTH_PERCENT;
    if (panelWidth <= halfViewport) return 0;
    const progress = (panelWidth - halfViewport) / (maxWidth - halfViewport);
    return Math.min(progress * 0.4, 0.4); // Max 40% opacity
  };

  // Single panel that changes style based on isExpanded
  return (
    <>
      {/* Backdrop that appears during resize - opacity based on width (only past 50% viewport) */}
      {(isResizing || panelWidth > window.innerWidth * 0.5) && !isExpanded && (
        <div 
          className="hidden lg:block fixed inset-0 z-[39] bg-black transition-opacity duration-150 pointer-events-none"
          style={{ opacity: getResizeBackdropOpacity() }}
        />
      )}
      {/* Overlay to prevent iframe from capturing mouse events during resize */}
      {isResizing && (
        <div className="fixed inset-0 z-[41] cursor-ew-resize" />
      )}
      {/* Backdrop for expanded mode */}
      {isExpanded && (
        <div 
          className="fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => setIsExpanded(false)}
        />
      )}
      
      {/* Panel - changes position/size based on isExpanded. Hidden on mobile unless expanded. */}
      <div 
        ref={panelRef}
        className={`fixed bg-card shadow-xl flex flex-col overflow-visible ${
          isExpanded 
            ? 'inset-0 lg:inset-4 z-[100] lg:rounded-xl' 
            : 'hidden lg:flex right-0 top-16 bottom-0 z-40 border-l border-border'
        }`}
        style={!isExpanded ? { width: `${panelWidth}px` } : undefined}
      >
        {/* Resize Handle - only in side panel mode */}
        {!isExpanded && (
          <div
            className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-primary/30 active:bg-primary/50 z-10 group transition-colors"
            onMouseDown={handleResizeStart}
          >
            {/* Visible grip indicator */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-20 flex flex-col items-center justify-center gap-1.5 opacity-50 group-hover:opacity-100 transition-opacity">
              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <>
                <div className="flex items-center gap-1 bg-background rounded-lg p-1">
                  <Button
                    variant={viewMode === 'desktop' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('desktop')}
                    className="h-8 px-3"
                  >
                    <Monitor className="w-4 h-4 mr-1" />
                    Desktop
                  </Button>
                  <Button
                    variant={viewMode === 'mobile' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('mobile')}
                    className="h-8 px-3"
                  >
                    <Smartphone className="w-4 h-4 mr-1" />
                    Mobile
                  </Button>
                </div>
                <span className="text-sm font-medium text-foreground ml-2">
                  {previewTheme ? `Preview — ${previewTheme.themeTitle}` : `Live Website — ${domain.slug}`}
                </span>
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">
                  {previewTheme ? 'Preview Website' : 'Live Website'}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!isExpanded && (
              <div className="flex items-center bg-background rounded-md p-0.5 mr-2">
                <Button
                  variant={viewMode === 'desktop' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('desktop')}
                  className="h-7 w-7 p-0"
                  title="Desktop view"
                >
                  <Monitor className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant={viewMode === 'mobile' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('mobile')}
                  className="h-7 w-7 p-0"
                  title="Mobile view"
                >
                  <Smartphone className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
            {!isExpanded && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(true)}
                className="h-7 w-7 p-0"
                title="Expand preview"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={openInNewTab}
              className={isExpanded ? "h-8 px-3" : "h-7 w-7 p-0"}
              title="Open in new tab"
            >
              <ExternalLink className={isExpanded ? "w-4 h-4 mr-1" : "w-3.5 h-3.5"} />
              {isExpanded && "Open"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={isExpanded ? () => setIsExpanded(false) : onClose}
              className={isExpanded ? "h-8 w-8 p-0" : "h-7 w-7 p-0"}
              title={isExpanded ? "Collapse preview" : "Close preview"}
            >
              <X className={isExpanded ? "w-4 h-4" : "w-3.5 h-3.5"} />
            </Button>
          </div>
        </div>

        {/* Iframe Container */}
        <div className={`flex-1 bg-muted/30 overflow-hidden flex items-center justify-center ${isExpanded ? 'p-4' : 'p-3 items-start'}`}>
          <div
            className={`bg-white rounded-lg shadow-md overflow-hidden h-full transition-all duration-300 relative flex flex-col ${
              viewMode === 'mobile' ? 'w-[375px]' : 'w-full'
            }`}
            style={viewMode === 'mobile' ? { maxWidth: '375px' } : {}}
          >
            {/* Browser Address Bar */}
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border-b border-border shrink-0">
              <div className="flex-1 flex items-center bg-background rounded-md px-3 py-1 text-xs text-muted-foreground">
                <span className="truncate" title={previewUrl || 'No URL'}>
                  {previewUrl ? previewUrl.split('?')[0] : 'No URL configured'}
                </span>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">
                {viewMode === 'mobile' ? '375px' : 'Full width'}
              </span>
            </div>
            
            {/* Iframe Content */}
            <div className="flex-1 relative">
              {!previewUrl ? (
                <div className="flex items-center justify-center h-full bg-muted/20">
                  <div className="text-center p-8">
                    <Eye className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                    <p className="text-muted-foreground font-medium">No website URL configured</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Please configure a domain URL to preview</p>
                  </div>
                </div>
              ) : (
                <>
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                    <div className="flex flex-col items-center gap-3 text-muted-foreground">
                      <Spinner size="lg" />
                      <span className="text-sm">Loading preview...</span>
                    </div>
                  </div>
                )}
                <iframe
                  key={iframeKey}
                  ref={iframeRef}
                  src={iframeSrc || previewUrl || ''}
                  className="w-full h-full border-0"
                  onLoad={handleIframeLoad}
                  title="Website Preview"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  style={{ pointerEvents: 'auto' }}
                />
              </>)
            }
            </div>
          </div>
        </div>

        {/* Preview Action Bar - ear tab protruding from panel edge at bottom */}
        {previewTheme && (
          <div className={`absolute bottom-20 z-20 animate-in duration-300 ${
            isExpanded 
              ? 'right-6 slide-in-from-bottom' 
              : 'left-0 -translate-x-full slide-in-from-right'
          }`}>
            <div className={`bg-primary backdrop-blur-md border border-primary shadow-xl px-5 py-4 relative whitespace-nowrap ${
              isExpanded ? 'rounded-2xl' : 'rounded-l-2xl'
            }`}>
              {/* Close button - top right */}
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1.5 right-1.5 h-6 w-6 p-0 rounded-full text-primary-foreground/70 hover:bg-primary-foreground/20 hover:text-primary-foreground"
                onClick={() => { setPreviewTheme?.(null); handleRefresh(); }}
                disabled={activatingTheme}
                title="Exit preview"
              >
                <X className="w-3.5 h-3.5" />
              </Button>

              <div className="flex flex-col items-center gap-3 pt-1">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-primary-foreground shrink-0" />
                  <span className="text-sm font-semibold text-primary-foreground select-none">
                    {previewTheme.themeTitle}
                  </span>
                </div>
                {canActivateTheme ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-9 rounded-lg text-xs px-5"
                    onClick={handleActivatePreviewTheme}
                    loading={activatingTheme}
                    loadingText="..."
                  >
                    <Check className="w-3.5 h-3.5 mr-1" />
                    Activate
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-9 rounded-lg text-xs px-5"
                    onClick={() => openUpgradeModal('themes')}
                  >
                    Upgrade to Activate
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Preview Toggle Button Component (for pages that don't show preview)
export const PreviewToggleButton: React.FC<{
  isOpen: boolean;
  onToggle: () => void;
  domain: Domain | null;
}> = ({ isOpen, onToggle, domain }) => {
  if (!domain) return null;

  return (
    <Button
      onClick={onToggle}
      variant={isOpen ? 'default' : 'outline'}
      size="sm"
      className="flex items-center gap-2"
    >
      {isOpen ? (
        <>
          <EyeOff className="w-4 h-4" />
          <span>Hide Preview</span>
        </>
      ) : (
        <>
          <Eye className="w-4 h-4" />
          <span>Show Preview</span>
        </>
      )}
    </Button>
  );
};

export default LivePreviewPanel;
